import { useEffect, useState } from "react";
import { X, Sparkles, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const FOR_WHO = [
  { id: "adult", label: "Adult" },
  { id: "teen", label: "Teen" },
  { id: "junior", label: "Junior" },
];

const EXPERIENCE = [
  { id: "never", label: "Never played" },
  { id: "beginner", label: "Beginner" },
  { id: "intermediate", label: "Intermediate" },
  { id: "experienced", label: "Experienced" },
];

const GOALS = [
  { id: "basics", label: "Learn the basics" },
  { id: "swing", label: "Improve my swing" },
  { id: "score", label: "Lower my score" },
  { id: "competition", label: "Prepare for competition" },
  { id: "not_sure", label: "Not sure yet" },
];

const PRIORITIES = [
  { id: "price", label: "Price" },
  { id: "location", label: "Location" },
  { id: "style", label: "Teaching style" },
  { id: "experience", label: "Coaching experience" },
  { id: "availability", label: "Availability" },
];

function track(event) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", event, { event_category: "coach_finder" });
  }
}

const initialAnswers = {
  for_who: "",
  experience: "",
  goal: "",
  priorities: [],
  notes: "",
  name: "",
  email: "",
  phone: "",
};

export default function CoachFinderWidget() {
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState(initialAnswers);
  const [state, setState] = useState({ status: "idle", message: "" });
  const [started, setStarted] = useState(false);

  // Lock body scroll while open (and on mobile for the slide-up panel)
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      track("coach_finder_open");
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    // reset success state after close animation would complete
    if (state.status === "success") {
      setTimeout(() => {
        setAnswers(initialAnswers);
        setState({ status: "idle", message: "" });
        setStarted(false);
      }, 300);
    }
  };

  const update = (key, value) => {
    if (!started) {
      setStarted(true);
      track("coach_finder_start");
    }
    setAnswers((a) => ({ ...a, [key]: value }));
  };

  const togglePriority = (id) => {
    if (!started) {
      setStarted(true);
      track("coach_finder_start");
    }
    setAnswers((a) => ({
      ...a,
      priorities: a.priorities.includes(id)
        ? a.priorities.filter((p) => p !== id)
        : [...a.priorities, id],
    }));
  };

  const canSubmit =
    answers.for_who &&
    answers.experience &&
    answers.goal &&
    answers.name.trim() &&
    answers.email.includes("@");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setState({
        status: "error",
        message: "Please fill the required fields (and a valid email).",
      });
      return;
    }
    setState({ status: "loading", message: "" });
    try {
      await api.post("/coach-finder", {
        ...answers,
        notes: answers.notes || null,
        phone: answers.phone || null,
        source: "widget",
      });
      track("coach_finder_submit");
      setState({ status: "success", message: "" });
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  return (
    <>
      {/* Floating trigger — hidden while panel is open */}
      {!open && (
        <button
          type="button"
          onClick={handleOpen}
          data-testid="coach-finder-open-btn"
          aria-label="Open the GolfStart coach finder"
          className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-40 group bg-forest text-cream-light border border-forest shadow-[0_8px_24px_-10px_rgba(0,0,0,0.35)] hover:bg-forest-dark transition-colors flex items-center gap-3 pl-4 pr-5 py-3 md:py-3.5"
        >
          <span className="h-7 w-7 bg-gold text-forest flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
          </span>
          <span className="text-left leading-tight">
            <span className="block font-sans text-[9px] tracking-[0.25em] uppercase text-cream-light/70">
              Need help finding a coach?
            </span>
            <span className="block font-sans text-sm tracking-wide text-cream-light group-hover:text-gold transition-colors">
              Find My Coach
            </span>
          </span>
        </button>
      )}

      {/* Overlay + slide-out panel */}
      {open && (
        <div
          data-testid="coach-finder-panel"
          className="fixed inset-0 z-50 flex items-end md:items-stretch md:justify-end"
        >
          <button
            type="button"
            aria-label="Close coach finder"
            onClick={handleClose}
            className="absolute inset-0 bg-forest/60 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]"
          />
          <aside className="relative w-full md:w-[480px] max-h-[92vh] md:max-h-none md:h-full bg-cream border-t md:border-t-0 md:border-l border-cream-border overflow-y-auto animate-[slideIn_0.25s_ease-out]">
            {/* Header */}
            <div className="sticky top-0 bg-cream border-b border-cream-border px-6 md:px-8 py-5 flex items-start justify-between gap-4">
              <div>
                <div className="eyebrow">Coach Finder</div>
                <h2 className="mt-2 font-serif text-2xl md:text-3xl leading-tight text-forest">
                  Find the right golf coach.
                </h2>
              </div>
              <button
                type="button"
                onClick={handleClose}
                data-testid="coach-finder-close-btn"
                aria-label="Close"
                className="h-9 w-9 flex items-center justify-center border border-cream-border text-forest-dark hover:text-gold hover:border-gold transition-colors flex-shrink-0"
              >
                <X className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </div>

            {state.status === "success" ? (
              <SuccessPanel onClose={handleClose} />
            ) : (
              <form onSubmit={onSubmit} className="px-6 md:px-8 py-6 space-y-9">
                <p className="font-sans text-sm leading-relaxed text-forest-muted">
                  Tell us a little about what you&rsquo;re looking for, and GolfStart will help
                  you narrow down your options.
                </p>

                <Question label="1 · Who are the lessons for?">
                  <Chips
                    name="for_who"
                    options={FOR_WHO}
                    value={answers.for_who}
                    onChange={(v) => update("for_who", v)}
                  />
                </Question>

                <Question label="2 · What's your golf experience?">
                  <Chips
                    name="experience"
                    options={EXPERIENCE}
                    value={answers.experience}
                    onChange={(v) => update("experience", v)}
                  />
                </Question>

                <Question label="3 · What's your main goal?">
                  <Chips
                    name="goal"
                    options={GOALS}
                    value={answers.goal}
                    onChange={(v) => update("goal", v)}
                  />
                </Question>

                <Question label="4 · What matters most? (pick any)">
                  <Chips
                    name="priorities"
                    options={PRIORITIES}
                    value={answers.priorities}
                    onChange={togglePriority}
                    multi
                  />
                </Question>

                <Question label="5 · Anything else to share? (optional)">
                  <textarea
                    rows={3}
                    maxLength={1000}
                    value={answers.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder="Preferred days, location, past lessons…"
                    data-testid="coach-finder-notes"
                    className="w-full bg-cream-light border border-cream-border px-4 py-3 font-sans text-sm text-forest-dark placeholder:text-forest-muted/70 focus:outline-none focus:border-gold transition-colors resize-none"
                  />
                </Question>

                <div className="pt-2 border-t border-cream-border">
                  <div className="eyebrow mb-5">Your details</div>
                  <div className="space-y-3">
                    <TextInput
                      testid="coach-finder-name"
                      type="text"
                      required
                      placeholder="Name"
                      value={answers.name}
                      onChange={(e) => update("name", e.target.value)}
                    />
                    <TextInput
                      testid="coach-finder-email"
                      type="email"
                      required
                      placeholder="Email"
                      value={answers.email}
                      onChange={(e) => update("email", e.target.value)}
                    />
                    <TextInput
                      testid="coach-finder-phone"
                      type="tel"
                      placeholder="Phone (optional)"
                      value={answers.phone}
                      onChange={(e) => update("phone", e.target.value)}
                    />
                  </div>
                </div>

                {state.status === "error" && (
                  <div
                    data-testid="coach-finder-error"
                    className="font-sans text-xs text-red-700 border border-red-200 bg-red-50 px-4 py-3"
                  >
                    {state.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit || state.status === "loading"}
                  data-testid="coach-finder-submit-btn"
                  className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {state.status === "loading" ? "Sending…" : "Get Coach Recommendations"}
                </button>

                <p className="font-sans text-[11px] leading-relaxed text-forest-muted">
                  Your details are shared with GolfStart only. We&rsquo;ll review your answers
                  and get back to you as soon as possible.
                </p>
              </form>
            )}
          </aside>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideIn {
          from { transform: translateY(16px); opacity: 0 }
          to   { transform: translateY(0);    opacity: 1 }
        }
        @media (min-width: 768px) {
          @keyframes slideIn {
            from { transform: translateX(16px); opacity: 0 }
            to   { transform: translateX(0);    opacity: 1 }
          }
        }
      `}</style>
    </>
  );
}

function Question({ label, children }) {
  return (
    <div>
      <label className="block font-sans text-[11px] tracking-[0.2em] uppercase text-forest-dark mb-3">
        {label}
      </label>
      {children}
    </div>
  );
}

function Chips({ name, options, value, onChange, multi = false }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = multi ? value.includes(o.id) : value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            data-testid={`coach-finder-${name}-${o.id}`}
            className={`px-4 py-2 border font-sans text-xs tracking-wide transition-colors ${
              active
                ? "bg-forest text-cream-light border-forest"
                : "bg-cream-light text-forest-dark border-cream-border hover:border-gold hover:text-gold"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function TextInput({ testid, ...rest }) {
  return (
    <input
      {...rest}
      data-testid={testid}
      className="w-full bg-cream-light border border-cream-border px-4 py-3 font-sans text-sm text-forest-dark placeholder:text-forest-muted/70 focus:outline-none focus:border-gold transition-colors"
    />
  );
}

function SuccessPanel({ onClose }) {
  return (
    <div
      data-testid="coach-finder-success"
      className="px-6 md:px-8 py-10 md:py-14 flex flex-col items-start"
    >
      <div className="h-12 w-12 bg-gold text-forest flex items-center justify-center">
        <Check className="h-5 w-5" strokeWidth={2} />
      </div>
      <h3 className="mt-6 font-serif text-3xl md:text-4xl text-forest leading-tight">
        Thanks — we&rsquo;ve got it.
      </h3>
      <p className="mt-5 font-sans text-base leading-relaxed text-forest-muted max-w-sm">
        We&rsquo;ll review what you&rsquo;re looking for and help you find a coach that fits
        your goals and preferences. Expect a reply by email in the next couple of days.
      </p>
      <button
        type="button"
        onClick={onClose}
        className="mt-8 btn-secondary"
        data-testid="coach-finder-success-close"
      >
        Close
      </button>
    </div>
  );
}
